/**
 * Read-only hook for accessing streaming message state from Redux.
 *
 * This hook provides a simple component interface to the new streaming system.
 * It does NOT contain any animation logic - all animation is handled by the
 * RTK Listener middleware (streamingListener.ts).
 *
 * Usage:
 * ```typescript
 * const { displayedText, isAnimating, isComplete } = useStreamingMessage(messageId);
 * ```
 *
 * @module useStreamingMessage
 */

import { useMemo } from 'react';
import { useAppSelector } from './reduxHooks';
import {
  selectDisplayedText,
  selectIsAnimating,
  selectIsComplete,
} from '../streaming/streamingSelectors';

/**
 * Return type for useStreamingMessage hook.
 */
export interface UseStreamingMessageResult {
  /**
   * The currently displayed text (animated).
   * This includes fact placeholders in the format [[FACT:xxx]].
   */
  displayedText: string;

  /**
   * Whether the message is currently animating.
   * True means the animation loop is active for this message.
   */
  isAnimating: boolean;

  /**
   * Whether the stream is complete.
   * True means no more chunks will arrive from the backend.
   */
  isComplete: boolean;
}

/**
 * Hook to read streaming message state from Redux.
 *
 * This is a read-only hook - it does not trigger animations or modify state.
 * All animation logic is handled by the RTK Listener middleware.
 *
 * @param messageId - The unique identifier for the message
 * @returns Object containing displayedText, isAnimating, and isComplete
 *
 * @example
 * ```typescript
 * function MessageComponent({ messageId }: { messageId: string }) {
 *   const { displayedText, isAnimating, isComplete } = useStreamingMessage(messageId);
 *
 *   return (
 *     <div>
 *       <p>{displayedText}</p>
 *       {isAnimating && <Spinner />}
 *       {isComplete && <CheckIcon />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useStreamingMessage(messageId: string): UseStreamingMessageResult {
  // Use selectors to read from Redux state
  // These selectors are memoized, so they only recompute when their inputs change
  const displayedText = useAppSelector((state) => selectDisplayedText(state, messageId));
  const isAnimating = useAppSelector((state) => selectIsAnimating(state, messageId));
  const isComplete = useAppSelector((state) => selectIsComplete(state, messageId));

  // Memoize the result object to prevent unnecessary re-renders
  // Only recreate when the actual values change
  const result = useMemo(
    () => ({
      displayedText,
      isAnimating,
      isComplete,
    }),
    [displayedText, isAnimating, isComplete]
  );

  return result;
}
